import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { Button, Input, Label } from '@artstorefronts/ui';
import { ADMIN_SET_BOUNTY_END, scheduleLabel, type AdminBounty } from './bounty-shared';

function localValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * When the bounty stops: "every day for five days, and that's it" (Taylor, 2026-11-03). It
 * stops paying the minute the end passes and returns to draft, so it can be scheduled
 * again rather than rebuilt.
 */
export function BountyEndDate({ bounty }: { bounty: AdminBounty }) {
  const [setEnd, { loading }] = useMutation(ADMIN_SET_BOUNTY_END);
  const [value, setValue] = useState(localValue(bounty.endsAt));

  const save = async (endsAt: string | null) => {
    try {
      await setEnd({ variables: { id: bounty.id, endsAt } });
      toast.success(endsAt ? 'End set.' : 'End cleared.');
      if (!endsAt) setValue('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to set the end');
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4" data-testid="bounty-end-date">
      <div>
        <p className="text-sm font-semibold text-gray-900">Ends</p>
        <p className="text-xs text-gray-500">
          {bounty.endsAt
            ? `Stops paying ${scheduleLabel(bounty.endsAt)}, then goes back to draft.`
            : 'Runs until you stop it. Set an end and it stops paying then, and goes back to draft.'}
        </p>
      </div>
      <div className="space-y-1">
        <Label htmlFor="b-ends-at">End at</Label>
        <Input
          id="b-ends-at"
          type="datetime-local"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          data-testid="bounty-end-input"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!value || loading} onClick={() => void save(new Date(value).toISOString())}>
          Set end
        </Button>
        {bounty.endsAt ? (
          <Button size="sm" variant="outline" disabled={loading} onClick={() => void save(null)}>
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  );
}
